/* eslint-disable no-magic-numbers */
/* eslint-disable max-lines */
// Copyright (c) 2022 Alaska Airlines. All right reserved. Licensed under the Apache-2.0 license
// See LICENSE in the project root for license information.

// ---------------------------------------------------------------------

// If using litElement base class
import { LitElement, html } from "lit-element";
import { classMap } from 'lit-html/directives/class-map';
import {ifDefined} from 'lit-html/directives/if-defined';

// Import touch detection lib
import "focus-visible/dist/focus-visible.min.js";
import styleCss from "./style-css.js";
import styleCssFixed from './style-fixed-css.js';

// Import icon
import chevronRight from '@alaskaairux/icons/dist/icons/interface/chevron-right_es6';
import chevronDown from '@alaskaairux/icons/dist/icons/interface/chevron-down_es6';

/**
 * The alaska-navmenuitem element provides users a way to create nav menu item at your navigation.
 *
 * @attr {Boolean} fixed - Uses fixed pixel values for element shape
 * @attr {String} cssClass - Applies designated CSS class to demo element - you want to delete me!
 * @attr {Boolean} selected - Sets the selected state of the navmenuitem
 * @attr {String} label - Sets the label of the navmenuitem
 * @attr {String} subLabel - Sets the subLabel of the navmenuitem
 * @attr {Boolean} expanded - Sets the expanded state of the navmenuitem
 * @attr {String} href - Sets the href of the navmenuitem
 * @attr {String} target - Sets the target of the navmenuitem
 * @attr {Boolean} noLabel - Sets the noLabel state of the navmenuitem
 * @attr {Boolean} noIcon - Sets the noIcon state of the navmenuitem
 * @attr {Boolean} marker - Sets the marker state of the navmenuitem
 * @attr {Boolean} horizontal - Sets the horizontal state of the navmenuitem
 * @attr {String} level - Sets the level of the navmenuitem
 * @attr {Boolean} group - Sets the group state of the navmenuitem
 * @attr {Boolean} groupItem - Sets the groupItem state of the navmenuitem
 * @attr {Boolean} ondark - Sets the ondark state of the navmenuitem
 * @attr {Boolean} expandedOnMaximized - Sets the expandedOnMaximized state of the navmenuitem
 */

// build the component class
class AlaskaNavmenuitem extends LitElement {
  constructor() {
    super();
    this.selected = false;
    this.expanded = false;
    this.group = false;
    this.groupItem = false;
    this.maxChildLevel = 2;

    /**
     * @private
     */
    this.tempTagLabel = "";

    /**
     * @private
     */
    this.initialCustomTagLabel = "";

    this.addEventListener('mouseleave', this.onMouseLeaveItem);
  }

  static get properties() {
    return {
      ...super.properties,
      selected: {
        type: Boolean,
        reflect: true
      },
      label: {
        type: String,
        reflect: true
      },
      subLabel: {
        type: String,
        reflect: true
      },
      expanded: {
        type: Boolean,
        reflect: true
      },
      href: {
        type: String,
        reflect: true
      },
      target: {
        type: String,
        reflect: true
      },
      noLabel: {
        type: Boolean,
        reflect: true
      },
      noIcon: {
        type: Boolean,
        reflect: true
      },
      marker: {
        type: Boolean
      },
      horizontal: {
        type: Boolean,
        reflect: true
      },
      level: {
        type: String,
        reflect: true
      },
      group: {
        type: Boolean,
        reflect: true
      },
      groupItem: {
        type: Boolean,
        reflect: true
      },
      ondark: {
        type: Boolean,
        reflect: true
      },
      expandOnMaximize: {
        type: Boolean,
        reflect: true
      }
    };
  }

  static get styles() {
    return [
      styleCss,
      styleCssFixed
    ];
  }

  firstUpdated() {
    this.initEventListener();
    this.setLevel();
    this.resetTabIndex();
    this.setTabIndex();
    this.resetClassNavItems('focused');

    const icons = this.querySelectorAll("auro-icon");
    icons.forEach((icon) => {
      icon.customColor = true;
    });

    if (this.groupItem) {
      this.slot = "groupItem";
    }

    // Handle nav Item when change into noLabel.
    if (this.noLabel) {
      this.onNoLabelPropertyChanged();
    }

    if (!this.hasChildren) {
      return;
    }

    setTimeout(() => {
      this.onExpandedChanged();
    });

    this.handleGroupItems();
  }

  /**
   * Set class name to all navItemContent.
   * @param {String} className - The class name.
   */
  setClassNavMenuItems(className) {
    const navMenu = this.parentElement;
    if (navMenu) {
      const navMenuItems = Array.from(navMenu.querySelectorAll('alaska-navmenuitem'));
      navMenuItems.forEach((item) => {
        const navContent = item.shadowRoot.querySelector('.navItemContent');
        if (navContent) {
          const link = navContent.querySelector('.itemLink');
          link.classList.add(className);
        }
      });
    }
  }

  /**
   * Initialized the event listener.
   */
  initEventListener() {
    const navItem = this.shadowRoot.querySelector('.navItemContent');

    if (navItem) {
      const itemLink = navItem.querySelector('.itemLink');

      if (itemLink) {
        itemLink.addEventListener('keydown', (event) => {
          this.setClassNavMenuItems('focused');
          this.toggleButtonKeydownHandler(event);
        });
        itemLink.addEventListener('keyup', (event) => {
          this.setClassNavMenuItems('focused');
          this.toggleButtonKeyupHandler(event);
        });
      }
    }
  }

  /**
   * Toggles the navItem’s state with the enter key.
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  toggleButtonKeydownHandler(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onKeyDown(event);
    }
  }

  /**
   * Toggles the navItem’s state with space key.
   * @param {KeyboardEvent} event - The keyboard event.
   * @returns {void}
   */
  toggleButtonKeyupHandler(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onKeyDown(event);
    }
  }

  /**
   * Generate Icon.
   * @param {*} icon - Icon.
   * @returns Html Icon.
   */
  generateIcon(icon) {
    this.dom = new DOMParser().parseFromString(icon.svg, 'text/html');
    return this.dom.body.firstChild;
  }

  /**
   * Function to render expand icon based on some conditions.
   * @returns Html.
   */
  renderExpandIcon() {
    if (this.hasChildren) {
      if (this.level === 0) {
        return this.generateIcon(chevronDown);
      }

      if (this.horizontal) {
        if (!this.groupItem) {
          if (this.expanded) {
            return this.generateIcon(chevronDown);
          }
          return this.generateIcon(chevronRight);
        }
        return this.generateIcon(chevronDown);
      }
      return this.generateIcon(chevronRight);
    }
    return undefined;
  }

  /**
   * Action handler when mouse enters this component.
   */
  onMouseHoverItem() {
    if (!this.hasChildren || !this.horizontal || this.level !== 0) {
      return;
    }
    this.expanded = true;
  }

  /**
   * Action handler when mouse leaves this component.
   */
  onMouseLeaveItem() {
    if (!this.hasChildren || !this.horizontal || this.level === 1) {
      return;
    }
    this.expanded = false;
  }

  /**
   * Function handler when "tag" slotted element is changed.
   * @returns {void}
   */
  handleTagSlot() {
    if (this.tempTagLabel.length === 0) {
      this.tempTagLabel = this.customTagElement.getAttribute('label').split(' ').
        shift();
      this.customTagElement.setAttribute('label', this.tempTagLabel);
    }

    this.customTagLabel = this.customTagElement.getAttribute('label');
  }

  /**
   * Function to render template HTML for link-based item.
   * @returns Html.
   */
  itemLinkTemplate() {
    const spanClass = {
      'two-digits': this.marker > this.maxMarker,
    };

    const noIconClass = {
      'no-icon': this.noIcon,
    };

    return html`
      <div class="navItemContent" @mouseenter="${this.onMouseHoverItem}">
        <a class="itemLink"
          href=${this.href || '#!'}
          @click="${(event) => this.onClick(event)}"
          @keydown="${(event) => this.onKeyDown(event)}"
          ?target=${this.target}>
          <div class="iconContent ${classMap(noIconClass)}">
            ${ifDefined(this.marker ? html`<div class="supContent"><span class="${classMap(spanClass)}"></span></div>` : null)}
            <slot class="icon" name="icon"></slot>
          </div>
          <div id="content">
            ${ifDefined(this.noLabel
      ? html`
              <sup>
                <slot @slotchange=${this.handleTagSlot} name="tag"></slot>
              </sup>
              `
      : html`
              <div class="contentContainer">
                <div class="labelContainer">
                  <span class="title">${this.label}</span>
                  <span class="sub">${this.subLabel}</span>
                </div>
                <sup>
                  <slot @slotchange=${this.handleTagSlot} name="tag"></slot>
                </sup>
              </div>
              <span id="expandIcon">
                ${this.renderExpandIcon()}
              </span>
            `)}
          </div>
        </a>
      </div>
    `;
  }

  /**
   * Function to render group menu from child with props 'group' & 'groupItem'.
   * @returns Html.
   */
  groupTemplate() {
    return html`
      <div class="groupContainer">
        <slot name="groupItem"></slot>
      </div>
    `;
  }

  /**
   * Function to render children based on 'expanded' state.
   * @returns Html.
   */
  childrenTemplate() {
    if (this.children.length > 0) {
      return html`
        <div class="childContainer">
          <slot></slot>
          ${this.groupTemplate()}
        </div>
      `;
    }
    return undefined;
  }

  /**
   * If element level is above configured maxChildLevel(2) and has child menu-item, it should not render.
   * @returns {Boolean} Boolean.
   */
  get isRenderAborted() {
    return Number(this.level) >= this.maxChildLevel && this.hasChildren;
  }

  /**
   * Handling the menu-item rendering for groupItem type.
   */
  handleGroupItems() {
    if (this.group) {
      const items = this.querySelectorAll("[groupItem]");

      items.forEach((element, index) => {
        const hr = document.createElement("div");
        hr.classList.add("groupItemDivider");
        hr.slot = "groupItem";
        if (index !== 0) {
          this.insertBefore(hr, element);
        }
      });
    }
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === "expanded") {
        this.onExpandedChanged();
      }

      if (propName === "selected") {
        if (oldValue === this.selected) {
          return;
        }

        if (this.selected) {
          this.changeSelectedState(true);
          this.markParentWithSelectedChild();
        }
      }

      if (this.groupItem) {
        this.expanded = true;
      }

      if (propName === "expandOnMaximize") {
        this.onExpandOnMaximizeChanged();
      }

      if (propName === "noLabel") {
        this.onNoLabelPropertyChanged();
      }
    });
  }

  /**
   * Getter for custom tag HTML element.
   * @returns {HTMLElement}
   */
  get customTagElement() {
    return [...this.children].find((element) => element.tagName.toLowerCase() === 'alaska-elitetags');
  }

  /**
   * Getter for custom tag label.
   * @returns {String}
   * @private
   */
  get customTagLabel() {
    // Set  custom tag label or noLabel navMenu item.
    if (this.noLabel) {
      // Set custom tag label for noLabel navMenu item.
      return this.initialCustomTagLabel;
    }
    return this.tempTagLabel;
  }

  /**
   * Setter for custom tag label.
   */
  set customTagLabel(val) {
    this.initialCustomTagLabel = val.substring(0, 1);
  }

  /**
   * Function handler when 'noLabel' property is changed.
   * @private
   */
  onNoLabelPropertyChanged() {
    if (this.customTagElement) {
      if (this.tempTagLabel.length === 0) {
        this.tempTagLabel = this.customTagElement.getAttribute('label');
      }

      this.customTagLabel = this.customTagElement.getAttribute('label');
      this.customTagElement.setAttribute('label', this.customTagLabel);
    }
  }

  /**
   * This will trigger action(s) when property 'expanded' is changed.
   */
  onExpandedChanged() {
    if (this.expanded) {
      // Fire event to the menu, that an item is expanded
      const evt = new CustomEvent("menu-item-expanded", {
        bubbles: true,
        cancelable: true,
        detail: "expanded"
      });
      this.dispatchEvent(evt);
      this.onHandleExpanded();
    } else {
      this.onHandleCollapsed();
    }
  }

  /**
   * This will trigger action(s) when property 'expandOnMaximize' is changed.
   */
  onExpandOnMaximizeChanged() {
    if (this.expandOnMaximize) {
      // Fire event to the menu
      const evt = new CustomEvent("menu-item-expand-on-maximize", {
        bubbles: true,
        cancelable: true,
        detail: "expand-on-maximize"
      });
      this.dispatchEvent(evt);
    }
  }

  /**
   * Action handler when menu-item is expanded.
   */
  onHandleExpanded() {
    const childContainer = this.shadowRoot.querySelector(".childContainer");
    const items = this.querySelectorAll("alaska-navmenuitem");
    // padding top + bottom of child container in group
    const totalYPadding = 34;
    const pad = this.group ? this.clientHeight + totalYPadding : 0;

    if (childContainer) {
      childContainer.style.height = `${Array.prototype.reduce.call(items, (payload, current) => {
        if (current.groupItem) {
          return payload;
        }
        return payload + (current.level >= 1 ? current.offsetHeight || 0 : 0);
      }, 0) + pad}px`;
    }
  }

  /**
   * Action handler when menu-item is collapsed.
   */
  onHandleCollapsed() {
    const childContainer = this.shadowRoot.querySelector(".childContainer");
    if (childContainer) {
      childContainer.style.height = '0px';
    }
  }

  /**
   * Action handler on menu-item selected state.
   * @param {*} selected - Selected state.
   * @param {*} sourceEvent - Source event.
   */
  changeSelectedState(selected, sourceEvent) {
    this.selected = selected;
    // Fire event to the menu, that an item is selected
    const evt = new CustomEvent("menu-item-selected", {
      bubbles: true,
      cancelable: true,
      detail: { sourceEvent }
    });
    this.dispatchEvent(evt);
  }

  /**
   * Get all menu-items has children.
   * @returns {Boolean} Boolean.
   */
  get hasChildren() {
    return Boolean(this.querySelector("alaska-navmenuitem"));
  }

  /**
   * Finds all parent items and adds an attribute that it has a child which is selected.
   */
  markParentWithSelectedChild() {
    let element = this.parentElement;
    while (element instanceof AlaskaNavmenuitem) {
      element.setAttribute('hasSelectedChild', true);
      element = element.parentElement;
    }
  }

  /**
   * Action handler on menu-item click.
   * @param {*} evt HTML Event.
   */
  onClick(evt) {
    this.resetTabIndex();
    this.setTabIndex();
    if (!this.hasChildren) {
      this.selected = true;
    } else {
      this.expanded = !this.expanded;
      this.expandOnMaximize = !this.expandOnMaximize;
      evt.preventDefault();
    }
  }

  /**
   * Action handler on menu-item click.
   * @param {*} evt HTML Event.
   */
  onKeyDown(evt) {
    if (evt.key === 'Enter' || evt.key === ' ') {
      this.resetTabIndex();
      this.setTabIndex();
      if (!this.hasChildren) {
        this.selected = true;
      } else {
        this.expanded = !this.expanded;
        this.expandOnMaximize = !this.expandOnMaximize;
        evt.preventDefault();
      }
    }
  }

  /**
   * Set tabindex from the navMenuItem level > 0.
   */
  setTabIndex() {
    // Need setTimeout to fix speed for the set tabIndex
    setTimeout(() => {
      const navMenu = this.parentElement;
      if (navMenu) {
        const childArray = Array.from(navMenu.querySelectorAll('alaska-navmenuitem'));
        childArray.forEach((child) => {
          if (child.level > 0 && !child.parentElement.hasAttribute('expanded')) {
            child.setAttribute('tabindex', '-1');
          }
        });
      }
    }, 300);
  }

  /**
   * Reset tabindex from the navMenuItem level > 0.
   */
  resetTabIndex() {
    Promise.resolve().then(() => {
      const childArray = Array.from(this.parentElement.querySelectorAll('alaska-navmenuitem'));
      childArray.forEach((child) => {
        child.removeAttribute('tabindex');
      });
    });
  }

  /**
   * Reset class attributes at navMenuItem.
   * @param {String} className Class name.
   */
  resetClassNavItems(className) {
    const navMenu = this.parentElement;
    if (navMenu) {
      const childArray = Array.from(navMenu.querySelectorAll('alaska-navmenuitem'));
      childArray.forEach((child) => {
        const navContent = child.shadowRoot.querySelector('.navItemContent');
        if (!navContent) {
          return;
        }
        const link = navContent.querySelector('.itemLink');
        link.classList.remove(className);
      });
    }
  }

  /**
   * Set this component level of depth.
   */
  setLevel() {
    let temp = 0;
    let element = this.parentElement;
    while (element instanceof AlaskaNavmenuitem) {
      temp += 1;
      element = element.parentElement;
    }
    this.level = temp;
  }

  // function that renders the HTML and CSS into  the scope of the component
  render() {
    if (this.isRenderAborted) {
      return undefined;
    }
    return html`
      ${this.itemLinkTemplate()}
      ${this.childrenTemplate()}
    `;
  }
}

// define the name of the custom component
if (!customElements.get("alaska-navmenuitem")) {
  customElements.define("alaska-navmenuitem", AlaskaNavmenuitem);
}
